import { useEffect, useState, useRef } from 'react';
import { MeshGradient } from '@paper-design/shaders-react';
import { Meteors } from '../components/shared/Meteors';

type Phase = 'rest' | 'damage' | 'hold' | 'unwarp' | 'heal' | 'fade';
type Colors6 = [string, string, string, string, string, string];

// ─── Phase colors ───────────────────────────────────────────────────────
const PHASE_COLORS: Record<Phase, Colors6> = {
  damage: ['#CC2020', '#FF3333', '#E02828', '#8B2020', '#6B1A1A', '#CC2020'],
  hold:   ['#5A3018', '#8B5528', '#6B3A1A', '#4A2A15', '#2D1A0E', '#6B4020'],
  unwarp: ['#3A2A1E', '#4D3828', '#3A3025', '#2D2A22', '#1E1E1A', '#3A3025'],
  heal:   ['#1A4A3A', '#50E6B4', '#2A6B5A', '#334155', '#1E293B', '#1A4A3A'],
  fade:   ['#8A9A60', '#C5B560', '#B8A55B', '#7A8840', '#4A5228', '#A8A058'],
  rest:   ['#F4C542', '#D4A62A', '#FFE466', '#B8860B', '#8B6E1A', '#E8A735'],
};
const PHASE_LERP_MS: Record<Phase, number> = {
  damage: 0, hold: 800, unwarp: 1200, heal: 1600, fade: 1800, rest: 1600,
};

/* Color helpers */
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) | (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)).toString(16).slice(1).toUpperCase();
}
function lerpColor(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a); const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}
function lerpColors(a: Colors6, b: Colors6, t: number): Colors6 {
  return a.map((c, i) => lerpColor(c, b[i], t)) as Colors6;
}

function useAnimatedMesh(phase: Phase) {
  const [colors, setColors] = useState<Colors6>(PHASE_COLORS.rest);
  const fromRef = useRef<Colors6>(PHASE_COLORS.rest);
  const rafRef = useRef<number>(0);
  useEffect(() => {
    const target = PHASE_COLORS[phase];
    const duration = PHASE_LERP_MS[phase];
    if (duration === 0) { fromRef.current = target; setColors(target); return; }
    cancelAnimationFrame(rafRef.current);
    const from = fromRef.current;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const next = lerpColors(from, target, ease);
      fromRef.current = next;
      setColors(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase]);
  return colors;
}

function usePhaseCycle(): Phase {
  const [phase, setPhase] = useState<Phase>('rest');
  useEffect(() => {
    const cycle = () => {
      setPhase('damage');
      setTimeout(() => setPhase('hold'), 200);
      setTimeout(() => setPhase('unwarp'), 1500);
      setTimeout(() => setPhase('heal'), 4000);
      setTimeout(() => setPhase('fade'), 7800);
      setTimeout(() => setPhase('rest'), 10000);
    };
    const first = setTimeout(cycle, 3000);
    let id: ReturnType<typeof setInterval>;
    const int = setTimeout(() => { id = setInterval(cycle, 18000); }, 3000);
    return () => { clearTimeout(first); clearTimeout(int); if (id) clearInterval(id); };
  }, []);
  return phase;
}

// ═══════════════════════════════════════════════════════════════════════
// PIXEL CREST — Crown, Crossed Swords, Shield w/ Frame, Ribbon, Emblem
// Canvas: 64 × 52 pixels. At 480px rendered, each pixel ≈ 7.5px — chunky.
// ═══════════════════════════════════════════════════════════════════════
const VB_W = 64;
const VB_H = 52;

// Palette (adapts to mesh tone for shield body; frame/decor are static)
const GOLD_LIGHT = '#FFE466';
const GOLD = '#F4C542';
const GOLD_DARK = '#B8860B';
const GOLD_SHADOW = '#6B4A0A';
const STEEL_LIGHT = '#D8DCE4';
const STEEL = '#9FA4B0';
const STEEL_DARK = '#4A4E58';
const RIBBON = '#A83F3F';
const RIBBON_DARK = '#6B2525';
const RIBBON_HILIGHT = '#D85050';
const OUTLINE = '#0a0d13';

// ─── Shield outer silhouette (heater shape) — 32px wide, centered ──────
// Returns the x-start and width of the shield at a given row, relative to shield top-left.
// Shield is positioned at (shieldOx, shieldOy) in the 64x52 canvas.
const SHIELD_OY = 8;  // top of shield
const SHIELD_OX = 16; // left of shield
const SHIELD_W = 32;
const SHIELD_H = 36;

function outerShieldRow(y: number): { x: number; w: number } | null {
  // y is relative to shield top
  if (y < 0 || y >= SHIELD_H) return null;
  if (y === 0) return { x: 5, w: 22 };
  if (y === 1) return { x: 3, w: 26 };
  if (y === 2) return { x: 2, w: 28 };
  if (y === 3) return { x: 1, w: 30 };
  if (y <= 22) return { x: 0, w: 32 };  // straight body
  if (y === 23) return { x: 1, w: 30 };
  if (y === 24) return { x: 1, w: 30 };
  if (y === 25) return { x: 2, w: 28 };
  if (y === 26) return { x: 3, w: 26 };
  if (y === 27) return { x: 4, w: 24 };
  if (y === 28) return { x: 5, w: 22 };
  if (y === 29) return { x: 6, w: 20 };
  if (y === 30) return { x: 8, w: 16 };
  if (y === 31) return { x: 10, w: 12 };
  if (y === 32) return { x: 12, w: 8 };
  if (y === 33) return { x: 13, w: 6 };
  if (y === 34) return { x: 14, w: 4 };
  if (y === 35) return { x: 15, w: 2 };
  return null;
}

function isOuterShield(x: number, y: number): boolean {
  // canvas coords → shield-local coords
  const lx = x - SHIELD_OX;
  const ly = y - SHIELD_OY;
  const row = outerShieldRow(ly);
  if (!row) return false;
  return lx >= row.x && lx < row.x + row.w;
}

// Inner panel (inset by 3 pixels) — where the mesh gradient shows
const BORDER_THICKNESS = 3;
function innerPanelRow(y: number): { x: number; w: number } | null {
  const outer = outerShieldRow(y);
  if (!outer) return null;
  if (y < BORDER_THICKNESS) return null;
  if (y >= SHIELD_H - BORDER_THICKNESS - 2) return null;  // leave room for V-point
  return {
    x: outer.x + BORDER_THICKNESS,
    w: Math.max(0, outer.w - BORDER_THICKNESS * 2),
  };
}

function isInnerPanel(x: number, y: number): boolean {
  const lx = x - SHIELD_OX;
  const ly = y - SHIELD_OY;
  const row = innerPanelRow(ly);
  if (!row) return false;
  return lx >= row.x && lx < row.x + row.w;
}

// Border = outer AND NOT inner
function isBorder(x: number, y: number): boolean {
  return isOuterShield(x, y) && !isInnerPanel(x, y);
}

// ─── Sword-in-stone emblem — pixel grid (14w × 15h) ────────────────────
// Positioned centered in the inner panel
const EMBLEM_OX = 25; // canvas x
const EMBLEM_OY = 17; // canvas y
const EMBLEM: number[][] = [
  //   0 1 2 3 4 5 6 7 8 9 A B C D
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0], // pommel
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0], // guard
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0], // guard wider
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0], // blade
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0], // stone starts (blade entering)
  [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], // stone widest
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
];

// ─── 4-point stars inside shield panel (decorative) ────────────────────
// Positioned in top corners of the inner panel
const STARS: Array<{ x: number; y: number }> = [
  { x: 22, y: 14 },  // top-left
  { x: 40, y: 14 },  // top-right
  { x: 22, y: 34 },  // bottom-left
  { x: 40, y: 34 },  // bottom-right
];

// ─── Crown — sits above shield, centered horizontally ──────────────────
// 10px wide × 4px tall, centered at x=32
const CROWN_OX = 27;
const CROWN_OY = 2;
const CROWN: number[][] = [
  [1, 0, 0, 1, 0, 0, 1, 0, 0, 1],  // 3 points
  [1, 0, 1, 1, 1, 1, 1, 1, 0, 1],  // fill between
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],  // band top
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 0],  // band bottom
  [0, 0, 1, 1, 1, 1, 1, 1, 0, 0],  // base
];
// Crown gems (small dark dots inside the band)
const CROWN_GEMS: Array<{ x: number; y: number }> = [
  { x: 30, y: 5 },
  { x: 32, y: 5 },
  { x: 34, y: 5 },
];

// ─── Crossed swords behind shield ──────────────────────────────────────
// Bresenham-style diagonal pixel lines
function plotLine(x0: number, y0: number, x1: number, y1: number): Array<{ x: number; y: number }> {
  const pixels: Array<{ x: number; y: number }> = [];
  const dx = Math.abs(x1 - x0); const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1; const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy; let x = x0; let y = y0;
  for (let i = 0; i < 200; i++) {
    pixels.push({ x, y });
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
  }
  return pixels;
}

// Left sword — tip up-left, hilt down-right (behind shield)
// Tip at (6, 10), hilt (pommel) at (22, 42)  [canvas coords]
const SWORD_LEFT_BLADE = plotLine(7, 11, 20, 32);
const SWORD_LEFT_BLADE_DOUBLE = plotLine(8, 11, 21, 32); // 2-wide blade
const SWORD_LEFT_TIP: Array<{ x: number; y: number }> = [
  { x: 5, y: 9 }, { x: 6, y: 10 }, { x: 7, y: 11 },
  { x: 4, y: 10 }, { x: 5, y: 11 },
];
// Hilt details (guard + grip + pommel) — at bottom-right of blade path
const SWORD_LEFT_GUARD: Array<{ x: number; y: number }> = [
  { x: 19, y: 34 }, { x: 20, y: 34 }, { x: 21, y: 34 }, { x: 22, y: 34 },
  { x: 20, y: 33 }, { x: 21, y: 33 },
];
const SWORD_LEFT_GRIP: Array<{ x: number; y: number }> = [
  { x: 20, y: 35 }, { x: 21, y: 35 },
  { x: 21, y: 36 }, { x: 22, y: 36 },
  { x: 22, y: 37 }, { x: 23, y: 37 },
];
const SWORD_LEFT_POMMEL: Array<{ x: number; y: number }> = [
  { x: 22, y: 38 }, { x: 23, y: 38 }, { x: 24, y: 38 },
  { x: 22, y: 39 }, { x: 23, y: 39 }, { x: 24, y: 39 },
];

// Right sword — mirror of left across x=32
const mirrorX = (p: { x: number; y: number }) => ({ x: 63 - p.x, y: p.y });
const SWORD_RIGHT_BLADE = SWORD_LEFT_BLADE.map(mirrorX);
const SWORD_RIGHT_BLADE_DOUBLE = SWORD_LEFT_BLADE_DOUBLE.map(mirrorX);
const SWORD_RIGHT_TIP = SWORD_LEFT_TIP.map(mirrorX);
const SWORD_RIGHT_GUARD = SWORD_LEFT_GUARD.map(mirrorX);
const SWORD_RIGHT_GRIP = SWORD_LEFT_GRIP.map(mirrorX);
const SWORD_RIGHT_POMMEL = SWORD_LEFT_POMMEL.map(mirrorX);

// ─── Ribbon banner — wraps across middle of shield ─────────────────────
// Left ribbon tail (extends off left of shield at middle height)
// Three horizontal bands: upper fold, main band, lower fold
const RIBBON_Y = 24; // canvas y
const RIBBON_LEFT_TAIL: Array<{ x: number; y: number; shade: 'light' | 'mid' | 'dark' }> = [
  // Upper band
  { x: 1, y: RIBBON_Y,     shade: 'light' },
  { x: 2, y: RIBBON_Y,     shade: 'mid' },
  { x: 3, y: RIBBON_Y,     shade: 'mid' },
  { x: 4, y: RIBBON_Y,     shade: 'mid' },
  { x: 5, y: RIBBON_Y,     shade: 'mid' },
  { x: 6, y: RIBBON_Y,     shade: 'mid' },
  { x: 7, y: RIBBON_Y,     shade: 'mid' },
  { x: 8, y: RIBBON_Y,     shade: 'mid' },
  { x: 9, y: RIBBON_Y,     shade: 'mid' },
  { x: 10, y: RIBBON_Y,    shade: 'mid' },
  { x: 11, y: RIBBON_Y,    shade: 'mid' },
  { x: 12, y: RIBBON_Y,    shade: 'mid' },
  { x: 13, y: RIBBON_Y,    shade: 'mid' },
  { x: 14, y: RIBBON_Y,    shade: 'dark' },
  // Middle band (taller)
  { x: 0, y: RIBBON_Y + 1, shade: 'light' },
  { x: 1, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 2, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 3, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 4, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 5, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 6, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 7, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 8, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 9, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 10, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 11, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 12, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 13, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 14, y: RIBBON_Y + 1, shade: 'mid' },
  { x: 15, y: RIBBON_Y + 1, shade: 'dark' },
  // Lower band
  { x: 1, y: RIBBON_Y + 2, shade: 'dark' },
  { x: 2, y: RIBBON_Y + 2, shade: 'dark' },
  { x: 3, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 4, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 5, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 6, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 7, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 8, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 9, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 10, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 11, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 12, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 13, y: RIBBON_Y + 2, shade: 'mid' },
  { x: 14, y: RIBBON_Y + 2, shade: 'dark' },
  // Swallow-tail notch on far left end
  { x: 2, y: RIBBON_Y + 3, shade: 'dark' },
  { x: 3, y: RIBBON_Y + 3, shade: 'dark' },
  { x: 4, y: RIBBON_Y + 3, shade: 'dark' },
  { x: 5, y: RIBBON_Y + 3, shade: 'dark' },
  { x: 11, y: RIBBON_Y + 3, shade: 'dark' },
  { x: 12, y: RIBBON_Y + 3, shade: 'dark' },
  { x: 13, y: RIBBON_Y + 3, shade: 'dark' },
];
const RIBBON_RIGHT_TAIL = RIBBON_LEFT_TAIL.map(p => ({ ...p, x: 63 - p.x }));

// ─── Render helpers ────────────────────────────────────────────────────
function Px({ x, y, fill, size = 1, opacity }: { x: number; y: number; fill: string; size?: number; opacity?: number }) {
  return <rect x={x} y={y} width={size} height={size} fill={fill} opacity={opacity} shapeRendering="crispEdges" />;
}

// ═══════════════════════════════════════════════════════════════════════
// THE CREST — one polished component
// ═══════════════════════════════════════════════════════════════════════
function PixelCrest({ phase, size = 480 }: { phase: Phase; size?: number }) {
  const uid = useRef(`cr-${Math.random().toString(36).slice(2, 7)}`).current;
  const mesh = useAnimatedMesh(phase);

  // Border pixels with edge classification
  const borderEdge: Array<{ x: number; y: number; light: boolean }> = [];
  const innerFill: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < VB_H; y++) {
    for (let x = 0; x < VB_W; x++) {
      if (isBorder(x, y)) {
        const onLightSide = !isOuterShield(x - 1, y) || !isOuterShield(x, y - 1);
        borderEdge.push({ x, y, light: onLightSide });
      } else if (isInnerPanel(x, y)) {
        innerFill.push({ x, y });
      }
    }
  }

  // Outer outline — hard dark pixels around the whole shield silhouette
  const hardOutline: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < VB_H; y++) {
    for (let x = 0; x < VB_W; x++) {
      if (!isOuterShield(x, y)) continue;
      // Edge if neighbor is outside shield
      if (!isOuterShield(x - 1, y) || !isOuterShield(x + 1, y) ||
          !isOuterShield(x, y - 1) || !isOuterShield(x, y + 1)) {
        hardOutline.push({ x, y });
      }
    }
  }

  // Emblem pixels (for the mask — these are SUBTRACTED from shield body to create sword-in-stone cutout silhouette)
  const emblemPixels: Array<{ x: number; y: number }> = [];
  for (let ey = 0; ey < EMBLEM.length; ey++) {
    for (let ex = 0; ex < EMBLEM[ey].length; ex++) {
      if (EMBLEM[ey][ex]) emblemPixels.push({ x: EMBLEM_OX + ex, y: EMBLEM_OY + ey });
    }
  }

  return (
    <svg width={size} height={size * VB_H / VB_W} viewBox={`0 0 ${VB_W} ${VB_H}`}
      shapeRendering="crispEdges" style={{ overflow: 'visible' }}>
      <defs>
        {/* Mask for mesh gradient — only fills the inner panel MINUS emblem silhouette */}
        <mask id={`${uid}-panelMask`}>
          <rect width={VB_W} height={VB_H} fill="black" />
          {innerFill.map((p, i) => <rect key={i} x={p.x} y={p.y} width={1} height={1} fill="white" />)}
          {emblemPixels.map((p, i) => <rect key={`e-${i}`} x={p.x} y={p.y} width={1} height={1} fill="black" />)}
        </mask>
      </defs>

      {/* ─── Crossed swords (BEHIND everything) ─── */}
      {/* Blade shadows first (dark steel) */}
      {SWORD_LEFT_BLADE.map((p, i) => <Px key={`lb-${i}`} x={p.x} y={p.y} fill={STEEL_DARK} />)}
      {SWORD_LEFT_BLADE_DOUBLE.map((p, i) => <Px key={`lbd-${i}`} x={p.x} y={p.y} fill={STEEL} />)}
      {SWORD_LEFT_TIP.map((p, i) => <Px key={`lt-${i}`} x={p.x} y={p.y} fill={STEEL_LIGHT} />)}
      {SWORD_RIGHT_BLADE.map((p, i) => <Px key={`rb-${i}`} x={p.x} y={p.y} fill={STEEL_DARK} />)}
      {SWORD_RIGHT_BLADE_DOUBLE.map((p, i) => <Px key={`rbd-${i}`} x={p.x} y={p.y} fill={STEEL} />)}
      {SWORD_RIGHT_TIP.map((p, i) => <Px key={`rt-${i}`} x={p.x} y={p.y} fill={STEEL_LIGHT} />)}

      {/* ─── Crown (top, behind shield top) ─── */}
      {CROWN.map((row, ry) => row.map((v, rx) =>
        v ? <Px key={`cr-${ry}-${rx}`} x={CROWN_OX + rx} y={CROWN_OY + ry} fill={ry === CROWN.length - 1 ? GOLD_DARK : GOLD_LIGHT} /> : null
      ))}
      {CROWN_GEMS.map((g, i) => <Px key={`gem-${i}`} x={g.x} y={g.y} fill={RIBBON_HILIGHT} />)}
      {/* Crown outline */}
      {CROWN.map((row, ry) => row.map((v, rx) => {
        if (!v) return null;
        const outs: Array<[number, number]> = [];
        const cx = CROWN_OX + rx; const cy = CROWN_OY + ry;
        if (!CROWN[ry - 1]?.[rx]) outs.push([cx, cy - 1]);
        if (!CROWN[ry + 1]?.[rx]) outs.push([cx, cy + 1]);
        if (!CROWN[ry]?.[rx - 1]) outs.push([cx - 1, cy]);
        if (!CROWN[ry]?.[rx + 1]) outs.push([cx + 1, cy]);
        return outs.map(([x, y], i) => <Px key={`col-${ry}-${rx}-${i}`} x={x} y={y} fill={OUTLINE} />);
      }))}

      {/* ─── Shield border (gold frame) ─── */}
      {borderEdge.map((p, i) => (
        <Px key={`bd-${i}`} x={p.x} y={p.y} fill={p.light ? GOLD_LIGHT : GOLD_DARK} />
      ))}
      {/* Border inner highlight pass — brighter on very top-left */}
      {borderEdge.filter(p => p.light).map((p, i) => (
        <Px key={`bdh-${i}`} x={p.x} y={p.y} fill={GOLD} opacity={0.7} />
      ))}

      {/* ─── Inner panel — mesh gradient, masked, emblem cut out ─── */}
      <foreignObject x="0" y="0" width={VB_W} height={VB_H} mask={`url(#${uid}-panelMask)`}>
        <div style={{ width: VB_W, height: VB_H }}>
          <MeshGradient width={VB_W} height={VB_H} colors={mesh}
            speed={0.3} distortion={0.4} swirl={0.4} grainMixer={0} grainOverlay={0} />
        </div>
      </foreignObject>

      {/* Inner panel edge — darker shade on the panel edge for depth */}
      {innerFill.filter(p => {
        return !isInnerPanel(p.x - 1, p.y) || !isInnerPanel(p.x, p.y - 1);
      }).map((p, i) => (
        <Px key={`ipe-${i}`} x={p.x} y={p.y} fill={GOLD_SHADOW} opacity={0.55} />
      ))}

      {/* ─── 4-point stars inside panel ─── */}
      {STARS.map((s, i) => (
        <g key={`star-${i}`}>
          <Px x={s.x} y={s.y - 1} fill={GOLD_LIGHT} />
          <Px x={s.x - 1} y={s.y} fill={GOLD_LIGHT} />
          <Px x={s.x} y={s.y} fill={GOLD_LIGHT} />
          <Px x={s.x + 1} y={s.y} fill={GOLD_LIGHT} />
          <Px x={s.x} y={s.y + 1} fill={GOLD_LIGHT} />
        </g>
      ))}

      {/* ─── Ribbon banner (across middle of shield) ─── */}
      {RIBBON_LEFT_TAIL.map((p, i) => {
        const color = p.shade === 'light' ? RIBBON_HILIGHT : p.shade === 'mid' ? RIBBON : RIBBON_DARK;
        return <Px key={`rl-${i}`} x={p.x} y={p.y} fill={color} />;
      })}
      {RIBBON_RIGHT_TAIL.map((p, i) => {
        const color = p.shade === 'light' ? RIBBON_HILIGHT : p.shade === 'mid' ? RIBBON : RIBBON_DARK;
        return <Px key={`rr-${i}`} x={p.x} y={p.y} fill={color} />;
      })}
      {/* Ribbon outline */}
      {[...RIBBON_LEFT_TAIL, ...RIBBON_RIGHT_TAIL].map((p, i) => {
        const allRibbon = new Set([...RIBBON_LEFT_TAIL, ...RIBBON_RIGHT_TAIL].map(q => `${q.x},${q.y}`));
        const outs: Array<[number, number]> = [];
        if (!allRibbon.has(`${p.x - 1},${p.y}`)) outs.push([p.x - 1, p.y]);
        if (!allRibbon.has(`${p.x + 1},${p.y}`)) outs.push([p.x + 1, p.y]);
        if (!allRibbon.has(`${p.x},${p.y - 1}`)) outs.push([p.x, p.y - 1]);
        if (!allRibbon.has(`${p.x},${p.y + 1}`)) outs.push([p.x, p.y + 1]);
        // Skip outline pixels that fall inside shield border (ribbon passes BEHIND border)
        return outs
          .filter(([ox, oy]) => !isOuterShield(ox, oy))
          .map(([ox, oy], j) => <Px key={`rbo-${i}-${j}`} x={ox} y={oy} fill={OUTLINE} />);
      })}

      {/* ─── Hard shield outline (on top of frame so it defines silhouette) ─── */}
      {hardOutline.map((p, i) => {
        const offsets: Array<[number, number]> = [];
        if (!isOuterShield(p.x - 1, p.y)) offsets.push([p.x - 1, p.y]);
        if (!isOuterShield(p.x + 1, p.y)) offsets.push([p.x + 1, p.y]);
        if (!isOuterShield(p.x, p.y - 1)) offsets.push([p.x, p.y - 1]);
        if (!isOuterShield(p.x, p.y + 1)) offsets.push([p.x, p.y + 1]);
        return offsets.map(([ox, oy], j) => <Px key={`ol-${i}-${j}`} x={ox} y={oy} fill={OUTLINE} />);
      })}

      {/* ─── Sword hilts (crossed swords, FRONT layer) — visible below shield ─── */}
      {SWORD_LEFT_GUARD.map((p, i) => <Px key={`lg-${i}`} x={p.x} y={p.y} fill={GOLD} />)}
      {SWORD_LEFT_GRIP.map((p, i) => <Px key={`lgp-${i}`} x={p.x} y={p.y} fill={STEEL_DARK} />)}
      {SWORD_LEFT_POMMEL.map((p, i) => <Px key={`lp-${i}`} x={p.x} y={p.y} fill={GOLD} />)}
      {SWORD_RIGHT_GUARD.map((p, i) => <Px key={`rg-${i}`} x={p.x} y={p.y} fill={GOLD} />)}
      {SWORD_RIGHT_GRIP.map((p, i) => <Px key={`rgp-${i}`} x={p.x} y={p.y} fill={STEEL_DARK} />)}
      {SWORD_RIGHT_POMMEL.map((p, i) => <Px key={`rp-${i}`} x={p.x} y={p.y} fill={GOLD} />)}
      {/* Sword outlines */}
      {[...SWORD_LEFT_GUARD, ...SWORD_LEFT_GRIP, ...SWORD_LEFT_POMMEL,
        ...SWORD_RIGHT_GUARD, ...SWORD_RIGHT_GRIP, ...SWORD_RIGHT_POMMEL].map((p, i) => {
        // Black outline around each sword detail pixel (where adjacent is empty and not shield)
        return null; // keep simple; the sword pixels themselves act as outline mass
      })}
    </svg>
  );
}

// ─── Preview Page ──────────────────────────────────────────────────────
export function LandingShieldPreview() {
  const phase = usePhaseCycle();

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#fff', padding: '40px 32px', position: 'relative' }}>
      <div className="absolute top-[5%] left-0 right-0 h-[35%] overflow-visible pointer-events-none z-[5]">
        <Meteors number={18} phase={phase} />
      </div>

      <div className="relative z-10" style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{
          fontFamily: "'Metamorphous', serif", fontSize: 36, textAlign: 'center',
          color: '#FFE466', textShadow: '0 0 16px rgba(244, 197, 66, 0.8)',
          letterSpacing: '0.15em', marginBottom: 8,
        }}>
          PALADIN CREST
        </h1>
        <div style={{
          fontFamily: "'VT323', monospace", fontSize: 18, textAlign: 'center',
          color: 'rgba(216, 220, 228, 0.7)', marginBottom: 32,
        }}>
          Full pixel-art crest: crown, crossed swords, framed shield, ribbon, sword-in-stone emblem.
          Phase: <span style={{ color: '#FFE466' }}>{phase.toUpperCase()}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <PixelCrest phase={phase} size={600} />
        </div>
      </div>
    </div>
  );
}
